import * as Highcharts from 'highcharts';
import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import More from 'highcharts/highcharts-more';
import SolidGauge from 'highcharts/modules/solid-gauge';
import { TranslateService } from '@ngx-translate/core';

More(Highcharts);
SolidGauge(Highcharts);

@Component({
    selector: 'toro-turf-pro-gauge',
    templateUrl: './turf-pro-gauge.component.html',
    styleUrls: ['./turf-pro-gauge.component.less']
})
export class TurfProGaugeComponent implements OnInit {
    @HostBinding('class') class = 'toro-maya-gauge'
    @ViewChild('chartContainer', { static: false }) chartContainer: ElementRef;

    @Input() maxHeightPx: number = 86;
    @Input() valueColor: string = '#2A2A2A';
    @Input() trackColor: string = '#EAEAEA';
    @Input() label: string = '--';
    @Input() labelVerticalOffsetPx: number = 29;

    private _minValue: number;
    @Input() set minValue(value: number) {
        this._minValue = value;
        this.setupGauge();
    }

    get minValue(): number {
        return this._minValue;
    }

    private _maxValue: number;
    @Input() set maxValue(value: number) {
        this._maxValue = value;
        this.setupGauge();
    }

    get maxValue(): number {
        return this._maxValue;
    }

    private _value: number;
    @Input() set value(val: number) {
        if (val == null) { return; }

        this._value = val;
        this.setupGauge();
    }

    get value(): number {
        return this._value;
    }

    protected Highcharts = Highcharts;
    protected gauge: Highcharts.Chart = null;
    protected gaugeOptions: any = null;
    protected chartContainerWidth = 0;
    protected chartContainerHeight = 0;
    protected isGaugeLoaded = false;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(private translate: TranslateService) { }

    ngOnInit() {
        this.setupGauge();
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private setWidthOfChartContainer() {
        if (!this.chartContainer) { return; }
        this.chartContainerWidth = this.chartContainer.nativeElement.offsetWidth - 2;
        this.chartContainerHeight = this.chartContainer.nativeElement.offsetHeight - 2;
    }

    protected clearGauge() {
        this.gauge = null;
        this.gaugeOptions = null;
    }

    private setupGauge() {
        if (this.minValue == null || this.maxValue == null || this.maxValue == 0 || this.value == null) {
            return;
        }

        this.setWidthOfChartContainer();
        if (this.chartContainerWidth == 0) {
            setTimeout(() => this.setupGauge(), 100);
            return;
        }

        const self = this;

        this.gaugeOptions = {
            chart: {
                type: 'solidgauge',
                backgroundColor: 'transparent',
                margin:[5,5,5,5],
                width: self.chartContainerWidth,
                events: {
                    load(event) {
                        self.gauge = event.target;
                    }
                }
            },
            title: undefined,
            tooltip: { enabled: false },
            credits: { enabled: false },

            pane: {
                center: ['50%', '50%'],
                size: '100%',
                startAngle: -140,
                endAngle: 140,
                background: {
                    // This is your full-circle “track”
                    outerRadius: '100%',
                    innerRadius: '80%',
                    shape: 'arc',
                    backgroundColor: 'transparent',
                    borderWidth: 0
                } as any
            },

            yAxis: {
                min: self.minValue,
                max: self.maxValue,
                lineWidth: 0,
                tickPositions: [], // no ticks
                labels: { enabled: false }
            },

            plotOptions: {
                solidgauge: {
                    dataLabels: { enabled: false },
                    linecap: 'round',     // rounded ends (supported in solidgauge)
                    stickyTracking: false
                }
            },

            series: [
                {
                    // Background track
                    type: 'solidgauge',
                    rounded: true,
                    enableMouseTracking: false,
                    data: [{
                        y: 100,
                        color: self.trackColor,
                        radius: '100%',
                        innerRadius: '75%'
                    }]
                },
                {
                    // Value arc
                    type: 'solidgauge',
                    rounded: self.value > 0,
                    name: self.translate.instant(self.label),
                    data: [{
                        // This is the “value arc” overlaying the full arc
                        y: self.value,
                        radius: '100%',
                        innerRadius: '75%',
                        color: self.valueColor,
                    }],
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        borderWidth: 0,
                        y: -(self.chartContainerHeight / 2 - self.labelVerticalOffsetPx), // vertical offset; tweak if needed
                        format: `
                            <div style="text-align:center; color:${self.valueColor}">
                              <div style="font-size:16px;font-weight:600; line-height: 2px;">
                                {y}
                              </div><br/>
                              <div style="font-size:10px; line-height: 4px;">
                                {series.name}
                              </div>
                            </div>
                          `
                    }
                }
            ]
        };

        this.isGaugeLoaded = true;
    }
}
